import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { NotificationService } from './notification.service';
import { NotificationDialogComponent } from './notification-dialog/notification-dialog.component';
import { NotificationSettingsComponent } from './notification-settings/notification-settings.component';
import { WebsocketService } from './websocket.service';
import { Subscription, interval } from 'rxjs';
import { AuthService } from 'src/app/views/auth/shared/auth.service';
import { ContactReminder } from 'src/app/domain/IContactActivity';
import { ReminderNotificationModalComponent } from '../../components/modals/reminder-notification-modal/reminder-notification-modal.component';

@Component({
  selector: 'app-notification',
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.sass'],
})
export class NotificationComponent implements OnInit, OnDestroy {
  notifications: any[] = [];
  unreadCount: number = 0;
  isLoading: boolean = true;
  private audio: HTMLAudioElement;
  private notificationSubscription: Subscription | null = null;
  private reminderSubscription: Subscription | null = null;
  private refreshInterval: Subscription | null = null;
  private isInitialized = false;
  private isConnecting = false;

  constructor(
    private notificationService: NotificationService,
    private dialog: MatDialog,
    private router: Router,
    private websocketService: WebsocketService,
    private authService: AuthService
  ) {
    this.audio = new Audio('../../../../assets/notification-sound/olhaAmensagem.mp3');
    this.audio.load();
  }

  ngOnInit() {
    if (this.isInitialized) {
      console.log('NotificationComponent já foi inicializado');
      return;
    }

    console.log('Inicializando NotificationComponent...');
    this.isInitialized = true;

    // Verificar se o usuário está autenticado antes de tentar conectar
    if (this.isUserAuthenticated()) {
      this.setupWebSocketConnection();
    } else {
      console.log('Usuário não autenticado, pulando conexão WebSocket');
    }

    this.loadNotifications();

    // Configura a atualização periódica a cada 30 segundos (30000ms)
    this.refreshInterval = interval(30000).subscribe(() => {
      this.loadNotifications();
    });
  }

  /**
   * Verifica se o usuário está autenticado
   */
  private isUserAuthenticated(): boolean {
    const user = this.authService.getCurrentUser();
    const token = localStorage.getItem('@gmt:access_token');

    if (!user || !token) {
      console.log('Usuário não autenticado - user:', !!user, 'token:', !!token);
      return false;
    }

    // Verificar se o token não está expirado
    try {
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      if (tokenData.exp && tokenData.exp < currentTime) {
        console.log('Token expirado');
        return false;
      }
    } catch (error) {
      console.log('Erro ao verificar token:', error);
      return false;
    }

    console.log('Usuário autenticado:', user.id);
    return true;
  }

  private async setupWebSocketConnection() {
    if (this.isConnecting) {
      console.log('Conexão WebSocket já em andamento...');
      return;
    }

    const user = this.authService.getCurrentUser();
    const partner = this.authService.getPartner();

    if (!user) {
      console.error('Usuário não autenticado para WebSocket');
      return;
    }

    const ids = {
      userId: user.id,
      customerId: user.customerId,
      partnerId: partner?.id || user.partnerId
    };

    console.log('Configurando conexão WebSocket com IDs:', ids);

    try {
      this.isConnecting = true;

      // Sempre tenta conectar, o serviço gerencia se já está conectado
      await this.websocketService.setupSocketConnection(ids);
      this.listenToWebSocketEvents();

    } catch (error) {
      console.error('Erro ao conectar WebSocket:', error);
    } finally {
      this.isConnecting = false;
    }
  }

  private listenToWebSocketEvents() {
    console.log('Configurando listeners de eventos WebSocket...');

    // Cancela subscriptions anteriores se existirem
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
      this.notificationSubscription = null;
    }
    if (this.reminderSubscription) {
      this.reminderSubscription.unsubscribe();
      this.reminderSubscription = null;
    }

    this.notificationSubscription = this.websocketService
      .listenForNotifications()
      .subscribe({
        next: (notification: any) => {
          this.handleNewNotification(notification);
        },
        error: (error) => {
          console.error('Erro ao receber notificação genérica:', error);
        }
      });

    this.reminderSubscription = this.websocketService
      .listenForReminders()
      .subscribe({
        next: (reminderData: ContactReminder) => {
          this.handleReminderDue(reminderData);
        },
        error: (error) => {
          console.error('Erro ao receber lembrete vencido:', error);
        }
      });
  }

  private handleNewNotification(notification: any) {
    this.notifications.unshift(notification);
    this.unreadCount++;

    this.playSound();
    this.animateBell();
  }

  private handleReminderDue(reminderData: ContactReminder) {
    this.dialog.open(ReminderNotificationModalComponent, {
      width: '450px',
      data: reminderData,
      disableClose: true
    });

    this.playSound();
  }

  loadNotifications() {
    // Só carrega notificações se o usuário estiver autenticado
    if (!this.isUserAuthenticated()) {
      console.log('Usuário não autenticado, pulando carregamento de notificações');
      return;
    }

    this.isLoading = true;
    this.notificationService.getUserNotifications().subscribe({
      next: (data) => {
        this.notifications = data;
        this.unreadCount = data.filter(n => !n.seen).length;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar notificações:', error);
        this.isLoading = false;
      },
    });
  }

  playSound() {
    try {
      this.audio.currentTime = 0;
      this.audio.volume = 0.5;
      this.audio.play().catch(error => {
        console.error('Erro ao tocar som:', error);
      });
    } catch (error) {
      console.error('Erro ao manipular áudio:', error);
    }
  }

  animateBell() {
    const bell = document.getElementById('notification-bell');
    if (bell) {
      bell.classList.remove('bell-animation');
      void bell.offsetWidth;
      bell.classList.add('bell-animation');

      setTimeout(() => {
        bell.classList.remove('bell-animation');
      }, 1000);
    } else {
      console.error('Elemento do sino não encontrado');
    }
  }

  openNotification(notification: any) {
    this.notificationService.markAsSeen(notification.id).subscribe(() => {
      this.notifications = this.notifications.filter(n => n.id !== notification.id);
      this.unreadCount = this.notifications.filter(n => !n.seen).length;

      const dialogRef = this.dialog.open(NotificationDialogComponent, {
        width: '400px',
        data: {
          ...notification,
          userInfo: {
            name: notification.createdBy?.name || notification.userName || 'Sistema',
            userId: notification.user?.id,
            customerId: notification.customer?.id,
            partnerId: notification.partner?.id
          }
        },
      });
      dialogRef.afterClosed().subscribe(() => {
        if (notification.redirectUrl) {
          this.router.navigate([notification.redirectUrl]);
        }
      });
    });
  }

  openNotificationSettings() {
    const dialogRef = this.dialog.open(NotificationSettingsComponent, {
      width: '100%',
      data: {
        type: 'config',
        element: { name: 'Configurações de Notificação', id: 'settings' }
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      console.log('Modal de configurações fechado');
    });
  }

  clearAll() {
    this.notificationService.markAllAsSeen().subscribe(() => {
      this.notifications.forEach(n => n.seen = true);
      this.unreadCount = 0;
      this.notifications = [];
    });
  }

  ngOnDestroy() {
    console.log('NotificationComponent sendo destruído...');
    this.isInitialized = false;
    this.isConnecting = false;

    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
      this.notificationSubscription = null;
    }
    if (this.reminderSubscription) {
      this.reminderSubscription.unsubscribe();
      this.reminderSubscription = null;
    }
    if (this.refreshInterval) {
      this.refreshInterval.unsubscribe();
      this.refreshInterval = null;
    }

    // Desconecta do WebSocket apenas se não há mais componentes usando
    this.websocketService.disconnect();
  }
}
